// Build an intuitive, human-readable project name from the current line
// configuration, the placeholder + display fallback shown when the customer
// hasn't named the project themselves. Pure synthesis from `config` + the
// active schema's labels, derived live (never persisted), so it keeps tracking
// the spec as the customer fills it in. Returns null when nothing identifying
// is configured yet (the caller then falls back to "Project N").
//
// Shape: "<product>, <size>", e.g. "Perfect-bound Book, 6″ × 9″",
// "Single Sheet, 3.5″ × 2″", or just "Labels & Stickers" before a size is
// picked.
//
// A line sitting on a product-picker PRESET names itself for the card the
// customer clicked, not for the family the card rides on: a dust jacket is a
// single_sheet underneath. The label comes from the same PRODUCT_PRESETS entry
// and the same translation key the card renders, so the title and the tile can
// never read as different products. A media line names itself for the carrier
// its own `media_type` picks ("USB" or "CD/DVD"), read through the same marker
// the media spec gates on. A custom packaging line has no finished-size field,
// so its typed inside size is the size clause: two sizes on one order are two
// items, and the size is what tells them apart.

import {
  ATTRIBUTE_KEYS,
  MEDIA_USE_CONFIG_KEY,
  humanizeToken,
  mediaUseForConfig,
  type FormFieldDto,
  type FormSchemaDto,
} from 'shared';
import i18n from 'i18next';

import { formatCustomSize } from './formatCustomSize.js';
import { PRODUCT_PRESETS, presetActive, type FieldValues } from './SchemaForm.js';

// The finished-size keys, in the order they read most naturally. Flat work's
// single `finished_size` comes first; for books the cover trim (the size that
// identifies a book at a glance) comes before the interior. The first one with
// a value supplies the size clause.
const SIZE_KEYS = ['finished_size', 'cover_finished_size', 'interior_finished_size'];
const CUSTOM_PACKAGING_DIMENSIONS_KEY = ATTRIBUTE_KEYS.custom_packaging_dimensions;

function findField(schema: FormSchemaDto, key: string): FormFieldDto | undefined {
  return schema.fields.find((f) => f.key === key);
}

// The clean product name for a project title: the schema's product_type option
// label with any trailing parenthetical stripped ("Single Sheet (Flyer,
// Brochure, Postcard)" → "Single Sheet"), falling back to a humanized token
// when the option isn't in the active schema.
function productLabel(schema: FormSchemaDto, productType: string): string {
  const raw = findField(schema, 'product_type')?.enum_options.find((o) => o.value === productType)?.label;
  if (!raw) return humanizeToken(productType);
  return raw.replace(/\s*\(.*\)\s*$/, '').trim();
}

// The size clause for the title — the label of the first set finished-size
// field, with the custom-size escape read back from its `<key>_custom`
// companion (the same convention SpecSummary uses). Null when no size is set.
function sizeClause(schema: FormSchemaDto, config: FieldValues): string | null {
  for (const key of SIZE_KEYS) {
    const value = config[key];
    if (typeof value !== 'string' || value === '') continue;
    const field = findField(schema, key);
    if (!field) continue;
    if (value === 'custom') {
      const custom = config[`${key}_custom`];
      if (typeof custom === 'string' && custom.trim() !== '') return formatCustomSize(custom);
      continue;
    }
    const label = field.enum_options.find((o) => o.value === value)?.label;
    if (label) return label;
  }
  return null;
}

export function deriveProjectName(schema: FormSchemaDto, config: FieldValues): string | null {
  // Reuses the same preset label key SchemaForm renders (quote:spec.preset.*),
  // so the derived name matches the product card. Pure util (no hook) → singleton.
  const preset = PRODUCT_PRESETS.find((p) => presetActive(p, config));
  if (preset) return i18n.t(`quote:spec.preset.${preset.id}`, { defaultValue: preset.label });

  const productType = typeof config.product_type === 'string' ? config.product_type : null;
  if (!productType) return null;

  const mediaUse = mediaUseForConfig(config) ?? config[MEDIA_USE_CONFIG_KEY];
  if (productType === 'media_replication' && (mediaUse === 'usb' || mediaUse === 'disc')) {
    return i18n.t(`quote:spec.mediaTitle.${mediaUse}`, {
      defaultValue: mediaUse === 'usb' ? 'USB' : 'CD/DVD',
    });
  }

  const product = productLabel(schema, productType);
  const packagingSize = config[CUSTOM_PACKAGING_DIMENSIONS_KEY];
  const size =
    productType === 'custom_packaging' && typeof packagingSize === 'string' && packagingSize.trim() !== ''
      ? packagingSize.trim()
      : sizeClause(schema, config);
  return size ? `${product}, ${size}` : product;
}
